import dayjs from "dayjs";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { getMonthlyReservation } from "@/apis/reservation";
import useCalendarStore from "@/store/calendarStore";
import type { DailyReservation, DailyReservationData } from "@/types/reservation";
import { cn } from "@/utils/cn";

const daysKo = ["일", "월", "화", "수", "목", "금", "토"];

const statusColor: Record<string, string> = {
  approve: "border-green-200 bg-green-50",
  request: "border-amber-200 bg-amber-50",
  reject: "border-red-200 bg-red-50",
  cancel: "border-gray-200 bg-gray-50",
};

const formatTime = (t: string) => {
  if (!t || t.length < 4) return "";
  return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
};

interface WeeklyViewProps {
  isDaySelected?: boolean;
  onDateClick?: (day: dayjs.Dayjs) => void;
}

const WeeklyView = ({ isDaySelected = true, onDateClick }: WeeklyViewProps) => {
  const [date, setDate, monthlyReservations, selectedPlace, setMonthlyReservations] = useCalendarStore(
    useShallow(state => [state.date, state.setDate, state.monthlyReservations, state.selectedPlace, state.setMonthlyReservations]),
  );

  const weekStart = date.startOf("week");
  const weekEnd = date.endOf("week");
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  // If week spans two months, ensure we have data for both
  useEffect(() => {
    if (!selectedPlace) return;
    if (weekStart.month() !== weekEnd.month()) {
      const load = async () => {
        const [month1, month2] = await Promise.all([
          getMonthlyReservation(weekStart.format("YYYYMM"), selectedPlace.plcCd),
          getMonthlyReservation(weekEnd.format("YYYYMM"), selectedPlace.plcCd),
        ]);
        const merged = [...(month1 || []), ...(month2 || [])];
        const map = new Map<string, DailyReservation>();
        merged.forEach(r => map.set(r.day, r));
        setMonthlyReservations(Array.from(map.values()));
      };
      load();
    }
  }, [weekStart.format("YYYYMM"), weekEnd.format("YYYYMM"), selectedPlace]);

  const getReservationsForDay = (day: dayjs.Dayjs): DailyReservationData[] => {
    if (!monthlyReservations) return [];
    const match = monthlyReservations.find(
      (r: DailyReservation) => dayjs(r.day).isSame(day, "day"),
    );
    return match ? match.data : [];
  };

  const handleDayClick = (e: React.MouseEvent, day: dayjs.Dayjs) => {
    e.stopPropagation();
    setDate(day);
    onDateClick?.(day);
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <div className="grid min-w-[640px] grid-cols-7">
        {/* Headers */}
        {weekDays.map((day, i) => {
          const isToday = day.isSame(dayjs(), "day");
          const isSelected = isDaySelected && day.isSame(date, "day");
          return (
            <div
              key={i}
              onClick={(e) => handleDayClick(e, day)}
              className={cn(
                "cursor-pointer border-b border-r border-gray-100 px-2 py-2 text-center text-xs font-medium transition-colors last:border-r-0 hover:bg-primary/5",
                isToday && "bg-primary/5",
                isSelected && "bg-primary/10",
                i === 0 && "text-red",
                i === 6 && "text-saturday",
              )}
            >
              <div className={cn("text-sm", isSelected && "font-bold text-primary")}>
                {day.format("M/D")}
              </div>
              <div className="mt-1 flex items-center justify-center">
                <span className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs",
                  isSelected && "bg-primary text-white",
                )}>
                  {daysKo[day.day()]}
                </span>
              </div>
            </div>
          );
        })}

        {/* Day columns */}
        {weekDays.map((day, i) => {
          const reservations = getReservationsForDay(day);
          const isToday = day.isSame(dayjs(), "day");
          const isDayCol = isDaySelected && day.isSame(date, "day");

          return (
            <div
              key={i}
              onClick={(e) => handleDayClick(e, day)}
              className={cn(
                "min-h-[12rem] cursor-pointer border-r border-gray-100 p-1 transition-colors last:border-r-0",
                isToday && "bg-primary/5",
                isDayCol && "bg-primary/10",
              )}
            >
              <div className="flex flex-col gap-1">
                {reservations.map((r) => {
                  const colorCls = statusColor[r.sttCd] || statusColor.approve;
                  return (
                    <div
                      key={r.rsvtId}
                      className={cn(
                        "rounded-md border p-1.5 text-[10px] leading-tight",
                        colorCls,
                      )}
                    >
                      <div className="font-medium text-gray-700">
                        {formatTime(r.startTime)}~{formatTime(r.endTime)}
                      </div>
                      <div className="mt-0.5 truncate font-medium text-gray-900">
                        {r.plcNm}
                      </div>
                      <div className="mt-0.5 truncate text-gray-500">
                        {r.useCnts}
                      </div>
                    </div>
                  );
                })}
                {reservations.length === 0 && (
                  <div className="py-4 text-center text-[10px] text-gray-300">-</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyView;
